/**
 * Global "sound effects enabled" preference — cross-platform factory.
 *
 * Per-device (like theme / leaderboard prefs, and modeled on
 * `sync-client/study-groups/leaderboard-prefs.ts`): each app calls
 * `createSoundPrefsStore` once at module load with its own storage adapter
 * (`localStorage` on web, AsyncStorage on mobile) and exports the returned
 * API. Both platforms get identical, reactive behavior from one
 * implementation — a Settings toggle and an in-session mute button stay in
 * step because they read the same Zustand store.
 *
 * `getSoundsEnabled` is what `core.ts` consumes via the `isEnabled` config —
 * it must stay synchronous and cheap since it runs on every play.
 */

import type { PackName } from 'uisfx';
import { create } from 'zustand';
import type { StateStorage } from 'zustand/middleware';
import { createJSONStorage, persist } from 'zustand/middleware';

import { DEFAULT_SOUND_PACK } from './cues';

type State = {
  soundsEnabled: boolean;
  /** Master volume, 0..1. */
  volume: number;
  /** Active uisfx pack. Defaults to `DEFAULT_SOUND_PACK`; the Sound Cues
   *  story writes this so an auditioned pack survives reloads before it is
   *  committed as the constant. */
  pack: PackName;
};

type Actions = {
  setSoundsEnabled(enabled: boolean): void;
  setVolume(volume: number): void;
  setPack(pack: PackName): void;
};

export const DEFAULT_SOUND_VOLUME = 0.7;

export type SoundPrefsApi = {
  /** Reactive read — for toggles and mute-button icons. */
  useSoundsEnabled(): boolean;
  /** Synchronous imperative read — the sound core's mute gate. */
  getSoundsEnabled(): boolean;
  /** Imperative write — Settings toggle / in-session mute button. */
  setSoundsEnabled(enabled: boolean): void;
  /** Reactive read — for the volume slider. */
  useVolume(): number;
  getVolume(): number;
  setVolume(volume: number): void;
  /** Reactive read — for the story's pack switcher. */
  usePack(): PackName;
  getPack(): PackName;
  setPack(pack: PackName): void;
};

export type CreateSoundPrefsStoreOptions = {
  /** Storage backend — pass `localStorage` on web, an AsyncStorage adapter on mobile. */
  storage: StateStorage;
  /** Persistence key (pin per platform so prefs survive refactors). */
  name: string;
  /** Zustand persist version for the named store. */
  version: number;
  /** Called on every enabled-state write AND after rehydration — the app
   *  wires this to `applySoundsEnabled` so muting silences immediately. */
  onEnabledChange?: (enabled: boolean) => void;
  /** Called on every volume write AND after rehydration — wired to
   *  `applySoundVolume`. */
  onVolumeChange?: (volume: number) => void;
  /** Called on every pack write AND after rehydration — wired to
   *  `applySoundPack`. */
  onPackChange?: (pack: PackName) => void;
};

export function createSoundPrefsStore(
  options: CreateSoundPrefsStoreOptions,
): SoundPrefsApi {
  const useStore = create<State & Actions>()(
    persist(
      (set) => ({
        soundsEnabled: true,
        volume: DEFAULT_SOUND_VOLUME,
        pack: DEFAULT_SOUND_PACK,
        setSoundsEnabled: (enabled) => {
          set({ soundsEnabled: enabled });
          options.onEnabledChange?.(enabled);
        },
        setVolume: (volume) => {
          const clamped = Math.max(0, Math.min(1, volume));
          set({ volume: clamped });
          options.onVolumeChange?.(clamped);
        },
        setPack: (pack) => {
          set({ pack });
          options.onPackChange?.(pack);
        },
      }),
      {
        name: options.name,
        version: options.version,
        storage: createJSONStorage(() => options.storage),
        // v1 stores predate the volume field.
        migrate: (persisted) => ({
          volume: DEFAULT_SOUND_VOLUME,
          pack: DEFAULT_SOUND_PACK,
          ...(persisted as Partial<State>),
        }),
        onRehydrateStorage: () => (state) => {
          // Push the persisted prefs into the player once they land
          // (AsyncStorage hydrates async; the player starts with defaults).
          if (!state) return;
          options.onEnabledChange?.(state.soundsEnabled);
          options.onVolumeChange?.(state.volume);
          options.onPackChange?.(state.pack);
        },
      },
    ),
  );

  return {
    useSoundsEnabled: () => useStore((s) => s.soundsEnabled),
    getSoundsEnabled: () => useStore.getState().soundsEnabled,
    setSoundsEnabled: (enabled) => useStore.getState().setSoundsEnabled(enabled),
    useVolume: () => useStore((s) => s.volume),
    getVolume: () => useStore.getState().volume,
    setVolume: (volume) => useStore.getState().setVolume(volume),
    usePack: () => useStore((s) => s.pack),
    getPack: () => useStore.getState().pack,
    setPack: (pack) => useStore.getState().setPack(pack),
  };
}
